<template>
  <div class="date-input" :class="getValidationClasses()">
    <template v-if="isMobile">
      <DateInputWithNativeInput v-if="useNativeDateInput" class="date-input__date"
        :min="min" :max="max" :label="label" :placeholder="placeholder"
        :value="value" :editable="editable" @input="commit" @click="click" />
      <DateInputWithNativeSelectors v-else class="date-input__date"
        :value="value" :min="min" :max="max" @input="commit" />
    </template>
    
    <div v-if="isTablet || isDesktop">
      <div class="date-input__label" v-if="label" v-html="label" />
      <div class="date-input__body" ref="input">
        <div class="date-input__icon" @click="toggle"><slot name="icon" /></div>
        <div class="date-input__value" @click.self="toggle">
          <span v-if="value.isZero()" class="date-input__placeholder" @click.self="toggle">{{ placeholder }}</span>
          <span v-else @click.self="toggle">{{ value.format('DD.MM.YYYY') }}</span>
          <DateInputDropdown v-if="dropdownActive" :mode="mode" :title="title" class="date-input__dropdown"
            :value="value" :min="min" :max="max" :autoCloseParent="$refs.input"
            @input="commit" @close="close" />
        </div>
      </div>
    </div>
    
    <slot name="not-editable-field" />
    <div v-if="errors.has(validationField(validateAs))" class="v-field-error">{{ errors.first(validationField(validateAs)) }}</div>
  </div>
</template>

<script>
import moment, { Moment } from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import CustomControlValidationMixin from '@/components/CustomControlValidationMixin'
import DateInputDropdown from '@/components/DateInputDropdown.vue'
import DateInputWithNativeInput from '@/components/DateInputWithNativeInput'
import DateInputWithNativeSelectors from '@/components/DateInputWithNativeSelectors.vue'
import MediaDetector from '@/utils/MediaDetector'

@Component({
  mixins: [
    MediaDetector,
    CustomControlValidationMixin,
  ],
  '$_veeValidate': {
    events: 'input',
  },
  components: {
    DateInputWithNativeSelectors,
    DateInputDropdown,
    DateInputWithNativeInput,
  },
  props: {
    value: {
      type: Moment,
      required: true,
    },
    min: {
      type: Moment,
      required: false,
      default: () => moment().subtract(3, 'years').startOf('day'),
    },
    max: {
      type: Moment,
      required: false,
      default: () => moment().startOf('day'),
    },
    placeholder: {
      type: String,
      required: false,
      default: 'DD.MM.RRRR',
    },
    mode: {
      // Mode for the desktop version
      type: String,
      required: false,
      default: 'year', // 'year', 'month', 'day'
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
    },
    validateAs: {
      type: String,
      required: false,
    },
    useNativeDateInput: {
      type: Boolean,
    },
    editable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
})
export default class DateInput extends Vue {
  dropdownActive = false

  toggle () {
    if (this.editable) {
      this.dropdownActive = !this.dropdownActive
      this.$emit('toggle', this.dropdownActive)
    } else {
      this.$emit('validateNonEditable')
    }
  }

  setValidatorFlags (value) {
    this.$validator.validate(this.validateAs, value) 
    this.$validator.fields.find({ name: this.validateAs }).setFlags({ 'changed': true })
  }

  commit (value) {
    this.$emit('input', moment(value))
    if (this.validateAs) this.setValidatorFlags(value)

    this.close()
  }

  click () {
    if (!this.editable) this.$emit('validateNonEditable')
    this.$emit('click')
  }

  close () {
    this.dropdownActive = false
  }
}
</script>

<style lang="scss">
@import './forms/FormValidationIndicator.scss';

.date-input {
  @include form-validation-indicator(18px, 35px);
  outline: none;

  @include mediaFluid(tablet) {
    &::before {
      top: 23px;
    }
  }

  &--blue-border {
    select,
    .date-input__body {
      border-bottom: none;
    }

    .selection-box, .date-input__body {
      background-image: url('/assets/images/input-box-underline-blue.png');
      background-position-x: right;
      background-position-y: bottom;
      background-repeat: no-repeat;
      user-select: none;
    }
  }

  &--child &__value,
  &--child &-native__value {
    font-family: 'Gotham';
    font-weight: bold;
    font-size: 12px;
    padding-top: 3px;
  }

  &--child::before {
    right: 5px;
  }

  &__body {
    border-bottom: 1px solid #0057A8;
    cursor: pointer;
  }

  &__value {
    position: relative;
  }

  &__placeholder {
    opacity: 0.6;
  }

  &__label {
    font-size: 10px;
  }

  &.field-invalid .figure--calendar,
  &.field-invalid &__label,
  &.field-invalid &__value {
    color: #E71F6C !important;
  }

  &.field-invalid .selection-box,
  &.field-invalid .date-input__body {
    border-bottom: 1px solid #E71F6C;
  }

  &.field-invalid.date-input--blue-border .selection-box,
  &.field-invalid.date-input--blue-border .date-input__body {
    background-image: url('/assets/images/input-box-underline-red.png');    
    border-bottom: none;
  }

  .v-not-editable span {
    color: #fff;
    text-transform: uppercase;
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, .5);
    cursor: pointer;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/DateInput.vue